import React from 'react';
import { Box, Container, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { IconChevronDown } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next'; // 1. Import hook

// Import your beautiful, high-resolution image
import heroImage from '../assets/images/lale.jpeg';

// --- (Styled Components & Animation Variants remain the same) ---
const PEACOCK_TEAL = 'rgb(0, 95, 105)';
const STONE_WHITE = '#FFFFFF';
const SHADOW_COLOR = 'rgba(0, 0, 0, 0.6)';

const HeroContainer = styled(Box)({
  position: 'relative',
  height: '90vh',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: STONE_WHITE,
  overflow: 'hidden',
});

const BackgroundImage = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: `url(${heroImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  zIndex: 1,
  animation: 'kenburns 20s ease-out infinite',
  '@keyframes kenburns': {
    '0%': { transform: 'scale(1) translate(0, 0)' },
    '50%': { transform: 'scale(1.1) translate(-2%, 2%)' },
    '100%': { transform: 'scale(1) translate(0, 0)' },
  },
});

const GradientOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: `linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, ${PEACOCK_TEAL} 90%)`,
  opacity: 0.7,
  zIndex: 2,
});

const ContentContainer = styled(Container)({
  position: 'relative',
  zIndex: 3,
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: "easeOut",
    },
  },
};

const AboutHero = () => {
  const { t } = useTranslation(); // 2. Initialize translation function

  return (
    <HeroContainer component="section">
      <BackgroundImage />
      <GradientOverlay />
      <ContentContainer
        maxWidth="lg"
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontFamily: '"Cormorant Garamond", "Georgia", serif',
              fontWeight: 700,
              fontSize: { xs: '2.8rem', sm: '4rem', md: '5.5rem' },
              lineHeight: 1.1,
              mb: 2,
              textShadow: `0 3px 12px ${SHADOW_COLOR}`,
            }}
          >
            {t('about_hero_headline')} {/* 3. Use translation */}
          </Typography>
        </motion.div>

        <motion.div variants={itemVariants}>
          <Typography
            variant="h6"
            component="p"
            sx={{
              fontFamily: '"Lato", "Helvetica", "Arial", sans-serif',
              fontSize: { xs: '1rem', sm: '1.2rem' },
              maxWidth: '700px',
              mx: 'auto',
              color: 'rgba(255, 255, 255, 0.95)',
              lineHeight: 1.8,
              textShadow: `0 2px 8px ${SHADOW_COLOR}`,
              mb: 4,
            }}
          >
            {t('about_hero_subheadline')} {/* 3. Use translation */}
          </Typography>
        </motion.div>

      </ContentContainer>
      
      <Box
        component={motion.div}
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        sx={{
          position: 'absolute',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3,
        }}
      >
        <IconButton sx={{ color: 'rgba(255,255,255,0.7)', animation: 'bounce 2s infinite' }}>
          <IconChevronDown size={32} />
        </IconButton>
      </Box>

      <style>
        {`
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
        `}
      </style>
    </HeroContainer>
  );
};

export default AboutHero;